import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import SelectUsersDialog from "@app/component/SelectEntityDialog/SelectUsersDialog";
import { isOwner } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import defaultIdGetter from "@app/util/defaultIdGetter";
import { userDisplayName } from "@app/util/formatters";

import messages from "../../messages";
import { selectUsers } from "../../store/actions";
import { getSelectedUsers } from "../../store/selectors";

interface Props {
  selectedUsers: User[];
  selectUsers: typeof selectUsers;
  teams: Team[];
  users: User[];
}

const UserSelector: React.FC<Props> = ({ selectedUsers, selectUsers, teams, users }) => {
  const intl = useIntl();
  const analyticIssuer = useAnalytics();

  const [visible, show, hide] = useBoolean();
  const buttonTitle = useMemo(() => {
    if (selectedUsers.length === 0) {
      return intl.formatMessage(messages.noUsers);
    }
    if (selectedUsers.length === users.length) {
      return intl.formatMessage(messages.allTeams);
    }
    if (selectedUsers.length === 1) {
      return userDisplayName(selectedUsers[0]);
    }
    if (selectedUsers.length > 1) {
      const allSelectedTeams: Team[] = [];
      const selectedUserIds = new Set(selectedUsers.map(({ id }) => id));
      const hasSelectedUsersWithNoTeam = users
        .filter(({ teams }) => teams.length === 0)
        .some(({ id }) => selectedUserIds.has(id));
      teams.forEach((team) => {
        if (
          team.users
            .filter((user) => !isOwner(user as User))
            .every(({ id }) => selectedUserIds.has(id))
        ) {
          allSelectedTeams.push(team);
        }
      });
      if (allSelectedTeams.length === 1 && !hasSelectedUsersWithNoTeam) {
        return allSelectedTeams[0].name;
      }
    }
    return intl.formatMessage(messages.multipleUsersOrTeams);
  }, [intl, selectedUsers, teams, users]);

  const handleConfirm = useCallback(
    (users: User[]) => {
      selectUsers(users.map(({ id }) => id));
      analyticIssuer.clicked(["Select Users"]);
    },
    [analyticIssuer, selectUsers]
  );

  return (
    <div>
      <Button onClick={show} rightIcon={<FontAwesomeIcon icon={faPen} size="2xs" />} type="link">
        {buttonTitle}
      </Button>
      <SelectUsersDialog
        entities={users}
        getDisplayName={userDisplayName}
        getKey={defaultIdGetter}
        onConfirm={handleConfirm}
        onHide={hide}
        selectedEntities={selectedUsers}
        title={intl.formatMessage({
          id: "home.cards.activityReps.selectUsersDialog.title",
          defaultMessage: "Select Users",
          description: "Title for select users dialog",
        })}
        visible={visible}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  selectedUsers: getSelectedUsers(state),
  teams: getTeams(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  selectUsers,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserSelector);
